import React, { useState, useCallback, useMemo } from 'react'
import { useEndpoints, useFeatures, useSubscriptionMutations } from 'src/api/queries/endpoints'
import { useCompanyContext } from 'src/companies/routes/company-context'
import {
  Placeholders,
  Column,
  Row,
  Box,
  Checkbox,
  Spinner,
  Card,
  CardContent,
  IconButton,
  ChevronRightIcon,
  ChevronDownIcon,
  Input,
  Button,
  DeleteIcon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Dialog,
  Text,
  Select,
} from 'src/ui'
import { useToast } from 'src/utils/toast'
import { useQueryClient } from '@tanstack/react-query'
import { FEATURE_GROUPS } from './feature-groups'
import { uniq } from 'lodash'
import { startReplay } from 'src/api/configurator'
import { useSelector } from 'react-redux'
import { RootState } from 'src/store'

interface SubscriptionsProps {
  env: string
}

const Subscriptions: React.FC<SubscriptionsProps> = ({ env }) => {
  const { company } = useCompanyContext()
  const storedEnvironments = useSelector(({ environments }: RootState) => environments.data)
  // Use the actual environment slug from the configurator API (e.g. "sand") rather than
  // the URL parameter (e.g. "sandbox"), which may differ.
  const [selectedEnvSlug, setSelectedEnvSlug] = useState<string>(storedEnvironments[0]?.slug ?? env)

  const { data: endpointsData, isLoading: isLoadingEndpoints } = useEndpoints()
  const { data: featuresData, isLoading: isLoadingFeatures } = useFeatures()
  const { addSubscription, removeSubscription } = useSubscriptionMutations()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [toggling, setToggling] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [newCustomEntity, setNewCustomEntity] = useState('')
  const [addedCustomEntities, setAddedCustomEntities] = useState<string[]>([])
  const [showReplayDialog, setShowReplayDialog] = useState(false)
  const [isReplaying, setIsReplaying] = useState(false)
  const [replayEndpoint, setReplayEndpoint] = useState<any>(null)
  const [replayFeature, setReplayFeature] = useState<string>('')

  const isSubscribed = useCallback(
    (endpoint: Endpoint, feature: string) => {
      return endpoint.subscriptions?.some(
        (sub) =>
          sub.company_slug === company.slug &&
          sub.environment === selectedEnvSlug &&
          sub.features.includes(feature)
      )
    },
    [company.slug, selectedEnvSlug]
  )

  const envOptions = useMemo(
    () => storedEnvironments.map((e) => ({ value: e.slug, label: e.name })),
    [storedEnvironments]
  )

  if (isLoadingEndpoints || isLoadingFeatures) return <Placeholders.LoadingState />
  if (!endpointsData?.data || !featuresData?.data) return null

  const endpoints = endpointsData.data
  const features = featuresData.data

  // Identify features that are part of a group to exclude from top-level list
  const groupedFeaturesSet = new Set<string>()
  Object.values(FEATURE_GROUPS).forEach((groupFeatures) => {
    groupFeatures.forEach((f) => groupedFeaturesSet.add(f))
  })

  // Prepare a unified list of items (either a Group Name or a Standalone Feature Name)
  // Standalone features: those that are not in any group
  const standaloneFeatures = features.filter((f) => !groupedFeaturesSet.has(f))

  // Groups
  const groups = Object.keys(FEATURE_GROUPS)

  // Combined and sorted list
  const unifiedList = [...standaloneFeatures, ...groups].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  )

  // Custom Features Logic
  const activeCustomFeatures = new Set<string>()
  endpoints.forEach((ep) => {
    ep.subscriptions?.forEach((sub) => {
      if (sub.company_slug === company.slug && sub.environment === selectedEnvSlug) {
        sub.features.forEach((f) => {
          if (f.includes('~')) {
            activeCustomFeatures.add(f)
          }
        })
      }
    })
  })

  const allCustomFeatures = uniq([
    ...Array.from(activeCustomFeatures),
    ...addedCustomEntities,
  ]).sort()

  const handleToggleFeature = async (
    endpoint: Endpoint,
    feature: string,
    isChecked: boolean,
    relatedFeatures: string[] = []
  ) => {
    const featuresToToggle = relatedFeatures.length > 0 ? relatedFeatures : [feature]
    const key = `${endpoint.id}-${feature || featuresToToggle[0]}`
    setToggling(key)

    try {
      const existingSub = endpoint.subscriptions?.find(
        (s) => s.company_slug === company.slug && s.environment === selectedEnvSlug
      )

      let nextFeatures = existingSub ? [...existingSub.features] : []

      if (isChecked) {
        featuresToToggle.forEach((f) => {
          if (!nextFeatures.includes(f)) nextFeatures.push(f)
        })
      } else {
        nextFeatures = nextFeatures.filter((f) => !featuresToToggle.includes(f))
      }

      const subscription = {
        company_slug: company.slug,
        environment: existingSub ? existingSub.environment : selectedEnvSlug,
        features: nextFeatures,
      }

      if (existingSub) {
        if (nextFeatures.length === 0) {
          await removeSubscription({ endpoint, subscription: existingSub })
        } else {
          await addSubscription({ endpoint, subscription })
        }
      } else if (isChecked) {
        await addSubscription({ endpoint, subscription })
      }

      await queryClient.invalidateQueries({ queryKey: ['endpoints'] })
      toast({ description: 'Subscription updated' })
    } catch (e) {
      console.error('Failed to update subscription:', e)
      toast({ status: 'error', description: 'Failed to update subscription' })
    } finally {
      setToggling(null)
    }
  }

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }))
  }

  const handleAddCustom = () => {
    if (!newCustomEntity) return
    const fullFeature = `${company.slug}~${newCustomEntity}`
    setAddedCustomEntities((prev) => [...prev, fullFeature])
    setNewCustomEntity('')
  }

  const handleRemoveCustom = async (feature: string) => {
    setAddedCustomEntities((prev) => prev.filter((f) => f !== feature))
    const endpointsToUnsubscribe = endpoints.filter((ep) => isSubscribed(ep, feature))

    if (endpointsToUnsubscribe.length > 0) {
      if (
        !globalThis.confirm(
          `This will unsubscribe ${endpointsToUnsubscribe.length} endpoint(s) from '${feature}'. Continue?`
        )
      ) {
        return
      }

      for (const endpoint of endpointsToUnsubscribe) {
        await handleToggleFeature(endpoint, feature, false)
      }
    }
  }

  const handleRemoveAllForEndpoint = async (endpoint: Endpoint) => {
    const existingSub = endpoint.subscriptions?.find(
      (s) => s.company_slug === company.slug && s.environment === selectedEnvSlug
    )
    if (!existingSub) return

    if (
      !globalThis.confirm(
        `This will remove all ${existingSub.features.length} feature subscriptions for endpoint '${endpoint.url}'. Continue?`
      )
    ) {
      return
    }

    const key = `${endpoint.id}-remove-all`
    setToggling(key)

    try {
      await removeSubscription({ endpoint, subscription: existingSub })
      await queryClient.invalidateQueries({ queryKey: ['endpoints'] })
      toast({ description: 'Successfully removed all subscriptions for endpoint' })
    } catch (error) {
      toast({ status: 'error', description: 'Failed to remove subscriptions' })
    } finally {
      setToggling(null)
    }
  }

  return (
    <Column>
      <Row mb={4} alignItems="center" justifyContent="space-between">
        <Box fontSize="lg" fontWeight="bold">
          Subscriptions
        </Box>
        {storedEnvironments.length > 1 && (
          <Row alignItems="center">
            <Box fontSize="sm" fontWeight="medium" color="gray.600" mr={2}>
              Environment:
            </Box>
            <Box width="200px">
              <Select
                options={envOptions}
                value={selectedEnvSlug}
                onChange={(option: any) => option && setSelectedEnvSlug(option.value)}
                isClearable={false}
                isSearchable={false}
              />
            </Box>
          </Row>
        )}
      </Row>

      <Card>
        <CardContent>
          <Row borderBottom="2px solid #e2e8f0" pb={2} mb={2} fontWeight="bold">
            <Box width="30%">Feature</Box>
            <Box width="70%">Endpoints</Box>
          </Row>

          {unifiedList.map((item, index) => {
            const isGroup = !!FEATURE_GROUPS[item]
            const isFirst = index === 0

            if (isGroup) {
              return (
                <SubscriptionGroup
                  key={item}
                  groupName={item}
                  endpoints={endpoints}
                  features={features}
                  isFirst={isFirst}
                  isSubscribed={isSubscribed}
                  handleToggleFeature={handleToggleFeature}
                  toggling={toggling}
                  toggleGroup={toggleGroup}
                  isExpanded={expandedGroups[item]}
                  companySlug={company.slug}
                  onReplay={(endpoint, feature) => {
                    setReplayEndpoint(endpoint)
                    setReplayFeature(feature)
                    setShowReplayDialog(true)
                  }}
                  onRemoveAll={handleRemoveAllForEndpoint}
                />
              )
            } else {
              // Standalone Feature
              const feature = item
              const supportedEndpoints = endpoints
                .filter(
                  (e) =>
                    e.supported_features === undefined ||
                    e.supported_features === null ||
                    e.supported_features.includes(feature)
                )
                .sort((a, b) => a.url.localeCompare(b.url))

              if (supportedEndpoints.length === 0) return null

              return (
                <FeatureRow
                  key={feature}
                  feature={feature}
                  endpoints={supportedEndpoints}
                  isSubscribed={isSubscribed}
                  handleToggle={(ep, f, c) => handleToggleFeature(ep, f, c)}
                  toggling={toggling}
                  isFirst={isFirst}
                  companySlug={company.slug}
                  onReplay={(endpoint, feature) => {
                    setReplayEndpoint(endpoint)
                    setReplayFeature(feature)
                    setShowReplayDialog(true)
                  }}
                  onRemoveAll={handleRemoveAllForEndpoint}
                />
              )
            }
          })}

          {/* Custom Entities Section */}
          <Box mt={6}>
            <Row borderTop="2px solid #e2e8f0" pt={4} pb={2} mb={2} fontWeight="bold" fontSize="md">
              Custom Entities
            </Row>

            {allCustomFeatures.map((feature) => {
              const sortedEndpoints = [...endpoints].sort((a, b) => a.url.localeCompare(b.url))

              return (
                <FeatureRow
                  key={feature}
                  feature={feature}
                  endpoints={sortedEndpoints}
                  isSubscribed={isSubscribed}
                  handleToggle={(ep, f, c) => handleToggleFeature(ep, f, c)}
                  toggling={toggling}
                  label={feature.replace(`${company.slug}~`, '')}
                  onDelete={() => handleRemoveCustom(feature)}
                  companySlug={company.slug}
                  onReplay={(endpoint, feature) => {
                    setReplayEndpoint(endpoint)
                    setReplayFeature(feature)
                    setShowReplayDialog(true)
                  }}
                />
              )
            })}

            <Row py={4} alignItems="center">
              <Box width="30%" pr={4}>
                <Input
                  size="sm"
                  placeholder="Entity Name (e.g. my-entity)"
                  value={newCustomEntity}
                  onChange={(e) => setNewCustomEntity(e.target.value)}
                />
              </Box>
              <Box>
                <Button size="sm" onClick={handleAddCustom} isDisabled={!newCustomEntity}>
                  Add Custom Entity
                </Button>
              </Box>
            </Row>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        title="Replay Subscription"
        isOpen={showReplayDialog}
        onClose={() => {
          setShowReplayDialog(false)
          setReplayEndpoint(null)
          setReplayFeature('')
        }}
        actions={
          <>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowReplayDialog(false)
                setReplayEndpoint(null)
                setReplayFeature('')
              }}
              mr={2}
            >
              Cancel
            </Button>

            <Button
              size="sm"
              colorScheme="primary"
              isLoading={isReplaying}
              onClick={async () => {
                setIsReplaying(true)
                // TODO: migrate to named function with useCallback

                const [replayErr] = await startReplay({
                  environment_slug: selectedEnvSlug,
                  company_slug: company.slug,
                  feature: replayFeature,
                  endpoint: {
                    url: replayEndpoint?.url,
                    token: replayEndpoint?.token,
                    id: replayEndpoint?.id,
                  },
                })

                setIsReplaying(false)

                if (replayErr) {
                  toast({
                    status: 'error',
                    description: 'Failed to start replay',
                  })
                  return
                }

                toast({ description: `Replay initiated for ${company.name} - ${replayFeature}` })
                setShowReplayDialog(false)
                setReplayEndpoint(null)
                setReplayFeature('')
              }}
            >
              Replay
            </Button>
          </>
        }
      >
        <Column spacing={3}>
          <Box>
            <Text fontWeight="bold" mb={1}>
              Company:
            </Text>
            <Text>
              {company.name} ({company.slug})
            </Text>
          </Box>
          <Box>
            <Text fontWeight="bold" mb={1}>
              Feature:
            </Text>
            <Text>{replayFeature}</Text>
          </Box>
          <Box>
            <Text fontWeight="bold" mb={1}>
              Endpoint:
            </Text>
            <Text fontSize="sm">{replayEndpoint?.url}</Text>
          </Box>
          <Box
            mt={2}
            p={3}
            bg="orange.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderColor="orange.400"
          >
            <Text fontSize="sm" color="orange.800">
              This will replay all events for this subscription.
            </Text>
          </Box>
        </Column>
      </Dialog>
    </Column>
  )
}

const SubscriptionGroup = ({
  groupName,
  endpoints,
  features,
  isFirst,
  isSubscribed,
  handleToggleFeature,
  toggling,
  toggleGroup,
  isExpanded,
  onReplay,
  companySlug,
  onRemoveAll,
}: any) => {
  const groupFeatures = FEATURE_GROUPS[groupName].filter((f) => features.includes(f))
  if (groupFeatures.length === 0) return null

  const getSupportedGroupFeatures = (endpoint: Endpoint) =>
    endpoint.supported_features === undefined || endpoint.supported_features === null
      ? groupFeatures
      : groupFeatures.filter((feature) => endpoint.supported_features?.includes(feature))

  // Filter endpoints that support ANY of the group features
  const supportedEndpoints = endpoints
    .filter((endpoint: Endpoint) => getSupportedGroupFeatures(endpoint).length > 0)
    .sort((a: any, b: any) => a.url.localeCompare(b.url))

  if (supportedEndpoints.length === 0) return null

  return (
    <React.Fragment>
      {/* Group Header Row */}
      <Row borderTop={isFirst ? 'none' : '1px solid #e2e8f0'} py={3}>
        <Box width="30%" pr={4} fontSize="sm" fontWeight="bold" color="gray.700">
          <Row alignItems="center">
            <IconButton
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
              size="xs"
              variant="ghost"
              onClick={() => toggleGroup(groupName)}
              mr={2}
            />
            {groupName}
          </Row>
        </Box>
        <Box width="70%">
          <Column>
            {supportedEndpoints.map((endpoint: Endpoint) => {
              const supportedGroupFeatures = getSupportedGroupFeatures(endpoint)

              // Aggregate state logic
              const childrenSubscribed = supportedGroupFeatures.map((feature) =>
                isSubscribed(endpoint, feature)
              )
              const allChildrenSubscribed = childrenSubscribed.every(Boolean)
              const someChildrenSubscribed = childrenSubscribed.some(Boolean)

              let isChecked = false
              let isIndeterminate = false

              if (allChildrenSubscribed) {
                isChecked = true
              } else if (someChildrenSubscribed) {
                isIndeterminate = true
              }

              const key = `${endpoint.id}-${supportedGroupFeatures[0]}`
              const isToggling = toggling === key

              return (
                <Row key={endpoint.id} alignItems="center" mb={1}>
                  <Checkbox
                    data-testid={`checkbox-${groupName}-${endpoint.id}`}
                    aria-label={`${groupName} - ${endpoint.url}`}
                    size="sm"
                    isChecked={isChecked}
                    isIndeterminate={isIndeterminate}
                    onChange={(e) =>
                      handleToggleFeature(
                        endpoint,
                        supportedGroupFeatures[0],
                        e.target.checked || isIndeterminate,
                        supportedGroupFeatures
                      )
                    }
                    isDisabled={isToggling}
                  >
                    <Box as="span" fontSize="sm">
                      {endpoint.url}
                    </Box>
                  </Checkbox>
                  {isToggling && (
                    <Box ml={2}>
                      <Spinner size="xs" />
                    </Box>
                  )}
                </Row>
              )
            })}
          </Column>
        </Box>
      </Row>

      {/* Expanded Children Rows */}
      {isExpanded && (
        <Box bg="gray.50" pl={8}>
          {groupFeatures.map((child) => (
            <FeatureRow
              key={child}
              feature={child}
              endpoints={supportedEndpoints}
              isSubscribed={isSubscribed}
              handleToggle={(ep: any, f: any, c: any) => handleToggleFeature(ep, f, c)}
              toggling={toggling}
              companySlug={companySlug}
              onReplay={onReplay}
              onRemoveAll={onRemoveAll}
            />
          ))}
        </Box>
      )}
    </React.Fragment>
  )
}

const FeatureRow = ({
  feature,
  endpoints,
  isSubscribed,
  handleToggle,
  toggling,
  label,
  onDelete,
  isFirst,
  onReplay,
  companySlug,
  onRemoveAll,
}: any) => {
  return (
    <Row py={3} borderTop={isFirst ? 'none' : '1px solid #e2e8f0'}>
      <Box width="30%" pr={4} fontSize="sm" fontWeight="bold" color="gray.700">
        <Row alignItems="center" justifyContent="space-between">
          <Box>{label || feature}</Box>
          {onDelete && (
            <IconButton
              icon={<DeleteIcon />}
              aria-label="Remove Custom Entity"
              size="sm"
              variant="ghost"
              isRound
              color="gray.600"
              onClick={onDelete}
            />
          )}
        </Row>
      </Box>
      <Box width="70%">
        <Column>
          {endpoints.map((endpoint) => {
            if (
              endpoint.supported_features &&
              !endpoint.supported_features.includes(feature) &&
              !feature.includes('~')
            ) {
              return null
            }

            const subscribed = isSubscribed(endpoint, feature)
            const key = `${endpoint.id}-${feature}`
            const isToggling = toggling === key
            const datadogLogsUrl = `https://app.datadoghq.com/logs?query=service%3Abushel-integrations-translator-endpoint%20container_name%3Aendpoint-${endpoint.id}%20%40context.feature%3A${feature}%20%40context.companySlug%3A${companySlug}&cols=service%2Cmessage%2C%40context.companySlug%2C%40context.feature%2C%40context.message_size%2C%40context.request_id&messageDisplay=inline&stream_sort=time%2Cdesc&viz=stream&live=true`

            return (
              <Row key={endpoint.id} alignItems="center" mb={1} justifyContent="space-between">
                <Row alignItems="center">
                  <Checkbox
                    data-testid={`checkbox-${feature}-${endpoint.id}`}
                    aria-label={`${feature} - ${endpoint.url}`}
                    size="sm"
                    isChecked={subscribed}
                    onChange={(e) => handleToggle(endpoint, feature, e.target.checked)}
                    isDisabled={isToggling}
                  >
                    <Box as="span" fontSize="sm">
                      {endpoint.url}
                    </Box>
                  </Checkbox>
                  {isToggling && (
                    <Box ml={2}>
                      <Spinner size="xs" />
                    </Box>
                  )}
                </Row>
                {subscribed && onReplay && (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label="More actions"
                      icon={<span>⋯</span>}
                      size="xs"
                      variant="ghost"
                    />
                    <MenuList>
                      <MenuItem
                        as="a"
                        href={datadogLogsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Datadog Logs
                      </MenuItem>
                      <MenuItem textTransform="none" onClick={() => onReplay(endpoint, feature)}>
                        Replay Subscription
                      </MenuItem>
                      {onRemoveAll && (
                        <MenuItem
                          textTransform="none"
                          onClick={() => onRemoveAll(endpoint)}
                          color="red.600"
                          _hover={{ bg: 'red.50' }}
                        >
                          Remove All Subscriptions
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                )}
              </Row>
            )
          })}
        </Column>
      </Box>
    </Row>
  )
}

export default Subscriptions
